import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import covertextBg from '../../assets/cover/covertextbg.png';
import spineImage from '../../assets/cover/koreshok.png';
import './BookCover.css';

/**
 * Shrinks a text element's font-size so it fits the available width
 * without breaking words. Used for the left/center plate presets where
 * a long word (like a title) should scale down rather than wrap mid-word
 * or overflow the cover.
 *
 * Re-runs when `text`, the container size, or `enabled` changes.
 */
function useFitText<T extends HTMLElement>(
  ref: React.RefObject<T | null>,
  text: string | undefined,
  enabled: boolean,
) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !enabled || !text) return;
    const parent = el.parentElement;
    if (!parent) return;

    const fit = () => {
      // Reset to whatever CSS asks for (handles container resize / preset switch).
      el.style.fontSize = '';
      const cs = getComputedStyle(el);
      const cssSize = parseFloat(cs.fontSize);
      if (!cssSize) return;
      const available = parent.clientWidth;
      // Allow scaling down to ~25% of CSS size before giving up.
      const minSize = Math.max(8, cssSize * 0.25);
      let size = cssSize;
      // scrollWidth > clientWidth means content overflows → shrink.
      while (el.scrollWidth > available + 0.5 && size > minSize) {
        size -= 1;
        el.style.fontSize = `${size}px`;
      }
    };

    fit();

    // Refit when the container resizes (3D pose change, viewport rotate, etc.).
    const ro = new ResizeObserver(() => fit());
    ro.observe(parent);
    return () => ro.disconnect();
  }, [ref, text, enabled]);
}

/**
 * Averages pixel colour of a remote image by rendering it into a tiny canvas.
 * Image must be same-origin (or have CORS headers).
 */
function useAverageColor(imageUrl: string): string | null {
  const [color, setColor] = useState<string | null>(null);

  useEffect(() => {
    if (!imageUrl) {
      setColor(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    let cancelled = false;

    img.onload = () => {
      if (cancelled) return;
      try {
        const SAMPLE = 8;
        const canvas = document.createElement('canvas');
        canvas.width = SAMPLE;
        canvas.height = SAMPLE;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, SAMPLE, SAMPLE);
        const data = ctx.getImageData(0, 0, SAMPLE, SAMPLE).data;
        let r = 0, g = 0, b = 0, n = 0;
        for (let i = 0; i < data.length; i += 4) {
          r += data[i];
          g += data[i + 1];
          b += data[i + 2];
          n += 1;
        }
        r = Math.round(r / n);
        g = Math.round(g / n);
        b = Math.round(b / n);
        setColor(`rgb(${r}, ${g}, ${b})`);
      } catch {
        // canvas tainted / cross-origin — ignore, fall back to default
      }
    };

    img.src = imageUrl;
    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  return color;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Цвет переплётной плоскости — зеркало серверного edgeRgb из
 * coverGenerator.ts: картинка center-crop'ится в квадрат, ужимается в
 * 32×32, берётся средний цвет 1-пиксельной рамки по периметру. Так PDF
 * выбирает цвет фона и корешка для custom-обложек.
 *
 * Локальные ассеты считаем через canvas; если картинку нельзя прочитать
 * (custom-фото на Spaces-CDN без CORS) — спрашиваем тот же цвет у бэка
 * (GET /upload/cover-color, там буквально серверный edgeRgb).
 */
function useEdgeColor(imageUrl: string, enabled: boolean): string | null {
  const [color, setColor] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !imageUrl) {
      setColor(null);
      return;
    }
    let cancelled = false;

    const fromApi = () => {
      if (!/^https?:\/\//i.test(imageUrl)) return;
      fetch(`${API_URL}/upload/cover-color?url=${encodeURIComponent(imageUrl)}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (!cancelled && d && typeof d.r === 'number') {
            setColor(`rgb(${d.r}, ${d.g}, ${d.b})`);
          }
        })
        .catch(() => {});
    };

    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onerror = () => {
      // CORS-хост без заголовков не даст даже загрузиться с
      // crossOrigin=anonymous — идём за цветом на бэк.
      if (!cancelled) fromApi();
    };

    img.onload = () => {
      if (cancelled) return;
      try {
        const SAMPLE = 32;
        const canvas = document.createElement('canvas');
        canvas.width = SAMPLE;
        canvas.height = SAMPLE;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        // fit: "cover", position: "centre" — как в sharp.resize на бэке.
        const side = Math.min(img.naturalWidth, img.naturalHeight);
        const sx = (img.naturalWidth - side) / 2;
        const sy = (img.naturalHeight - side) / 2;
        ctx.drawImage(img, sx, sy, side, side, 0, 0, SAMPLE, SAMPLE);
        const data = ctx.getImageData(0, 0, SAMPLE, SAMPLE).data;
        let r = 0, g = 0, b = 0, n = 0;
        const acc = (x: number, y: number) => {
          const i = (y * SAMPLE + x) * 4;
          r += data[i];
          g += data[i + 1];
          b += data[i + 2];
          n += 1;
        };
        for (let x = 0; x < SAMPLE; x += 1) {
          acc(x, 0);
          acc(x, SAMPLE - 1);
        }
        for (let y = 1; y < SAMPLE - 1; y += 1) {
          acc(0, y);
          acc(SAMPLE - 1, y);
        }
        setColor(`rgb(${Math.round(r / n)}, ${Math.round(g / n)}, ${Math.round(b / n)})`);
      } catch {
        // canvas tainted — цвет с бэка
        fromApi();
      }
    };

    img.src = imageUrl;
    return () => {
      cancelled = true;
    };
  }, [imageUrl, enabled]);

  return color;
}

export interface BookCoverProps {
  /** Фоновое изображение обложки */
  background: string;
  /** Название книги */
  title?: string;
  /** Имя автора (под названием на табличке) */
  author?: string;
  /** Внутренний отступ от краёв обложки, в процентах */
  inset?: number;
  /** Дополнительный наклон сверху (3D rotateX) */
  tilt?: boolean;
  /** Отрисовать плоской 2D-обложкой без 3D-перспективы */
  flat?: boolean;
  /** Зафиксировать 3D-позу: ни idle-анимации, ни реакции на курсор. */
  frozen?: boolean;
  /** Скрыть имитацию корешка */
  hideSpine?: boolean;
  /** Layout for the title/author block on the cover. Defaults to `frame`. */
  platePreset?: PlatePreset;
  /** Показать полиграфические поля как в PDF-обложке: одноцветная
   *  переплётная плоскость по краям, картинка с inset и кремовой обводкой. */
  printPlate?: boolean;
  /** Цвет переплётной плоскости для printPlate (каталожные обложки —
   *  plateColorForCover). Если не задан — вычисляется с краёв картинки. */
  plateColor?: string;
  /** Обработчик клика */
  onClick?: () => void;
  /** Дополнительные классы */
  className?: string;
}

export type PlatePreset = 'frame' | 'frame-top' | 'left' | 'center';

export const PLATE_PRESETS: PlatePreset[] = ['frame', 'frame-top', 'left', 'center'];

/** Пресеты с декоративной подложкой (covertextbg.png): отличаются только
 *  тем, где она стоит — по центру обложки или сверху. Вёрстка текста
 *  внутри подложки у них общая. */
export const isFramedPreset = (p: PlatePreset): boolean =>
  p === 'frame' || p === 'frame-top';

/** Проверка значения из БД/стораджа. Всё неизвестное — undefined. */
export function asPlatePreset(v: unknown): PlatePreset | undefined {
  return PLATE_PRESETS.includes(v as PlatePreset) ? (v as PlatePreset) : undefined;
}

export const BookCover: React.FC<BookCoverProps> = ({
  background,
  title,
  author,
  inset = 12,
  tilt = false,
  flat = false,
  frozen = false,
  hideSpine = false,
  platePreset = 'frame',
  printPlate = false,
  plateColor,
  onClick,
  className = '',
}) => {
  const titleRef = useRef<HTMLDivElement>(null);
  const authorRef = useRef<HTMLDivElement>(null);
  const fitText = !isFramedPreset(platePreset);
  useFitText(titleRef, title, fitText);
  useFitText(authorRef, author, fitText);

  const classes = [
    'bc',
    tilt ? 'bc--tilt' : '',
    flat ? 'bc--flat' : '',
    hideSpine ? 'bc--no-spine' : '',
    onClick ? 'bc--clickable' : '',
    printPlate ? 'bc--print' : '',
    className,
  ].filter(Boolean).join(' ');

  const spineColor = useAverageColor(background);
  const edgeColor = useEdgeColor(background, printPlate && !plateColor);

  const style: React.CSSProperties = {
    ['--bc-inset' as any]: `${inset}%`,
    ['--bc-plate-bg' as any]: `url(${covertextBg})`,
  };
  if (spineColor) {
    (style as any)['--bc-spine-color'] = spineColor;
  }
  // Плоскость и корешок — одна заливка, цвет считается как в
  // PDF-генераторе (edgeRgb). Пока edge-цвет не готов — средний цвет.
  const printPlateColor = plateColor ?? edgeColor ?? spineColor;
  if (printPlate && printPlateColor) {
    (style as any)['--bc-plate-color'] = printPlateColor;
    (style as any)['--bc-spine-color'] = printPlateColor;
  }
  if (!flat) {
    style.touchAction = 'none';
  }

  // ========== pointer-driven rotation + idle sway ==========

  const bookRef = useRef<HTMLDivElement>(null);
  const activeTouchId = useRef<number | null>(null);

  const modeRef = useRef<'idle' | 'active' | 'returning'>('idle');
  const lastActiveRef = useRef<{ ry: number; rx: number }>({ ry: 0, rx: -2 });
  const returnStartRef = useRef<number>(0);
  const returnFromRef = useRef<{ ry: number; rx: number }>({ ry: 0, rx: -2 });
  const startTimeRef = useRef<number>(0);

  const BASE_RY = 0;
  const BASE_RX = tilt ? -4 : -2;
  const MAX_RY = 32;
  const MAX_RX = 18;

  const IDLE_RY_AMP = 28;
  const IDLE_RX_AMP = 14;
  const IDLE_RY_FREQ = 0.42;
  const IDLE_RX_FREQ = 0.32;
  const RETURN_DURATION = 800;

  useEffect(() => {
    if (flat || frozen) return;
    let rafId: number;
    startTimeRef.current = performance.now();

    const tick = (now: number) => {
      const el = bookRef.current;
      if (el) {
        const t = (now - startTimeRef.current) / 1000;
        const ryIdle = Math.sin(t * IDLE_RY_FREQ) * IDLE_RY_AMP;
        const rxIdle = Math.cos(t * IDLE_RX_FREQ) * IDLE_RX_AMP;
        const targetRy = BASE_RY + ryIdle;
        const targetRx = BASE_RX + rxIdle;

        if (modeRef.current === 'idle') {
          el.style.transform = `rotateY(${targetRy}deg) rotateX(${targetRx}deg)`;
        } else if (modeRef.current === 'returning') {
          const elapsed = now - returnStartRef.current;
          const p = Math.min(1, elapsed / RETURN_DURATION);
          const ease = 1 - Math.pow(1 - p, 3);
          const ry = returnFromRef.current.ry + (targetRy - returnFromRef.current.ry) * ease;
          const rx = returnFromRef.current.rx + (targetRx - returnFromRef.current.rx) * ease;
          el.style.transform = `rotateY(${ry}deg) rotateX(${rx}deg)`;
          if (p >= 1) modeRef.current = 'idle';
        }
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flat, tilt, frozen]);

  const moveFromPointer = (clientX: number, clientY: number, target: HTMLElement) => {
    const rect = target.getBoundingClientRect();
    let relX = (clientX - rect.left) / rect.width - 0.5;
    let relY = (clientY - rect.top) / rect.height - 0.5;
    relX = Math.max(-0.5, Math.min(0.5, relX));
    relY = Math.max(-0.5, Math.min(0.5, relY));
    const ry = BASE_RY - relX * (MAX_RY * 2);
    const rx = BASE_RX + relY * (MAX_RX * 2);
    const el = bookRef.current;
    if (el) {
      el.style.transform = `rotateY(${ry}deg) rotateX(${rx}deg)`;
    }
    modeRef.current = 'active';
    lastActiveRef.current = { ry, rx };
  };

  const releaseToIdle = () => {
    modeRef.current = 'returning';
    returnStartRef.current = performance.now();
    returnFromRef.current = { ...lastActiveRef.current };
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (flat || frozen) return;
    if (e.pointerType === 'touch' && activeTouchId.current !== e.pointerId) return;
    moveFromPointer(e.clientX, e.clientY, e.currentTarget);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (flat || frozen) return;
    if (e.pointerType !== 'mouse') {
      e.currentTarget.setPointerCapture(e.pointerId);
      activeTouchId.current = e.pointerId;
      moveFromPointer(e.clientX, e.clientY, e.currentTarget);
    }
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (flat || frozen) return;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    if (activeTouchId.current === e.pointerId) activeTouchId.current = null;
    releaseToIdle();
  };

  const handlePointerLeave = (e: React.PointerEvent<HTMLDivElement>) => {
    if (flat || frozen) return;
    if (e.pointerType === 'mouse') releaseToIdle();
  };

  const handlePointerCancel = (e: React.PointerEvent<HTMLDivElement>) => {
    if (flat || frozen) return;
    if (activeTouchId.current === e.pointerId) activeTouchId.current = null;
    releaseToIdle();
  };

  return (
    <div
      className={classes}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      style={style}
      onPointerMove={handlePointerMove}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerLeave}
      onPointerCancel={handlePointerCancel}
    >
      <div className="bc__book" ref={bookRef}>
        {!flat && <div className="bc__back" aria-hidden />}

        {!hideSpine && !flat && (
          <div className="bc__spine-3d" aria-hidden>
            <img src={spineImage} alt="" className="bc__spine-img" />
          </div>
        )}

        {!flat && <div className="bc__pages-top-3d" aria-hidden />}
        {!flat && <div className="bc__pages-bottom-3d" aria-hidden />}
        {!flat && <div className="bc__pages-right-3d" aria-hidden />}

        {!flat && <div className="bc__board-front-top" aria-hidden />}
        {!flat && <div className="bc__board-front-right" aria-hidden />}
        {!flat && <div className="bc__board-front-bottom" aria-hidden />}

        {!flat && <div className="bc__board-back-top" aria-hidden />}
        {!flat && <div className="bc__board-back-right" aria-hidden />}
        {!flat && <div className="bc__board-back-bottom" aria-hidden />}

        <div className="bc__front">
          {background && (
            <img src={background} alt="" className="bc__bg" draggable={false} />
          )}

          {!flat && <div className="bc__front-grain" aria-hidden />}
          {!flat && <div className="bc__front-sheen" aria-hidden />}

          {(title !== undefined || author) && (
            <div className={`bc__frame bc__frame--${platePreset}`}>
              <div className={`bc__plate bc__plate--${platePreset}`}>
                {title !== undefined && (
                  <div className="bc__title-wrap">
                  <div ref={titleRef} className={`bc__title bc__title--${platePreset}`}>{title || ' '}</div>
                </div>
              )}
              {isFramedPreset(platePreset) && author && (
                <div className={`bc__author bc__author--${platePreset}`}>{author}</div>
              )}
            </div>
            {!isFramedPreset(platePreset) && author && (
              <div ref={authorRef} className={`bc__author bc__author--${platePreset}`}>{author}</div>
            )}
          </div>
          )}

          {!hideSpine && <div className="bc__spine-shade" aria-hidden />}
        </div>
      </div>
    </div>
  );
};
